import type { GoalPlatformStateResponse } from "@repo/shared/types";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./goal-platform-field.i18n";

type GoalPlatformFieldProps = {
  /** Server-owned eligibility for the goal. Renders nothing until the collection has platforms. */
  platformState: GoalPlatformStateResponse | null | undefined;
  /** The platform id shown as selected ("" = none). */
  value: string;
  onValueChange: (platformId: string) => void;
  disabled?: boolean;
  id?: string;
  "data-testid"?: string;
};

/**
 * QP-195 — the platform field shared by the goal page's details widget and the
 * Goal List modal, with the same four states everywhere: an unresolved hint, a
 * "none eligible" warning, a read-only pill when exactly one platform is
 * eligible (and it is the saved one, or none is saved), and a select otherwise. Presentational — the owner decides whether
 * a pick applies immediately (widget) or on Save (modal).
 */
export function GoalPlatformField({
  platformState,
  value,
  onValueChange,
  disabled = false,
  id = "goal-platform",
  "data-testid": testId = "goal-platform-section",
}: GoalPlatformFieldProps) {
  const localize = useLocalize();
  const t = useT(T);
  if (!platformState?.hasPlatforms) return null;

  let control: React.ReactNode;
  if (!platformState.resolved) {
    control = <p className="text-sm text-muted-foreground">{t.platformUnresolvedHint}</p>;
  } else if (platformState.eligible.length === 0) {
    control = (
      <p className="text-sm text-warning border border-warning/50 bg-warning/10 rounded-md px-3 py-2">
        {t.noPlatformAvailable}
      </p>
    );
  } else if (
    platformState.eligible.length === 1 &&
    // QL-546 — a saved platform that is no longer eligible (e.g. a balance
    // crossed a threshold) falls through to the select, so the field never
    // shows a pill that differs from the stored platform.
    (!value || value === platformState.eligible[0]!.id)
  ) {
    control = (
      <div>
        <span className="inline-flex items-center rounded-full border bg-muted px-3 py-1 text-sm font-medium">
          {localize(platformState.eligible[0]!.labelI18n)}
        </span>
      </div>
    );
  } else {
    control = (
      <Select
        // Empty string keeps the Select controlled when nothing is chosen
        // (Radix shows the placeholder for ""). A saved platform that is no
        // longer eligible has no item, so it shows the placeholder too, not a
        // blank trigger.
        value={platformState.eligible.some((p) => p.id === value) ? value : ""}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectTrigger id={id} className="w-full sm:w-72">
          <SelectValue placeholder={t.selectPlatformPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          {platformState.eligible.map((platform) => (
            <SelectItem key={platform.id} value={platform.id}>
              {localize(platform.labelI18n)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      <Label htmlFor={id}>
        {t.platform} <span className="text-destructive">*</span>
      </Label>
      {control}
    </div>
  );
}
